import { Component, effect, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { debounceTime, distinctUntilChanged, Subject } from "rxjs";
import { Contact } from "../../models/contact";
import { FormsModule } from "@angular/forms";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { UserStore } from "../../stores";
import { ContactCredit, ContactsCreditTransfer, SharedContactRepository } from "../../repositories/shared-contact-repository";
import { CentsToDollarsPipe } from "../../pipes/cents-to-dollars.pipe";

@Component({
	selector: "jhm-give-credits",
	templateUrl: "./give-credits.component.html",
	styleUrls: ["./give-credits.component.scss"],
	imports: [
		MatFormFieldModule,
		MatInputModule,
		FormsModule,
		CentsToDollarsPipe
	]
})
export class GiveCreditsComponent implements OnDestroy, OnInit
{
	@Output()
	public exit: EventEmitter<void> = new EventEmitter<void>();

	@Input()
	public initialContacts: Contact[] = [];

	public isOpen: boolean = false;
	public contacts: Contact[];
	public addedContacts: Contact[] = [];
	public searchQueryUpdate: Subject<Event> = new Subject<Event>();
	public searchQuery: string;
	public creditMap: Map<string, number> = new Map();
	public givingCreditCount: number = 0;
	public transferStep: string = "INIT";
	public ngDestroyed$: Subject<any> = new Subject();
	public userCredits: number;

	readonly userStore = inject(UserStore);

	public constructor(private contactRepository: SharedContactRepository)
	{
		this.contactRepository = contactRepository;
		this.contactRepository.ready.subscribe();

		this.searchQueryUpdate.pipe(
			debounceTime(500),
			distinctUntilChanged())
			.subscribe(() =>
			{
				this.getContactsFromQuery();
			});

		effect(() => {
			this.userCredits = this.userStore.credits();
		})
	}

	ngOnInit(): void
	{
		this.initialContacts.forEach((contact: Contact) =>
		{
			this.addContact(contact);
		});
	}

	ngOnDestroy(): void
	{
		this.ngDestroyed$.next(true);
	}

	public cancel(): void
	{
		this.exit.emit();
	}

	public giveCredits(): void
	{
		this.transferStep = "TRANSFERRING";
		const contactsCreditTransfers: ContactCredit[] = [];
		this.creditMap.forEach((value: number, key: string) =>
		{
			contactsCreditTransfers.push(new ContactCredit(key, value));
		});
		const contactsCreditTransfer: ContactsCreditTransfer = new ContactsCreditTransfer(contactsCreditTransfers);
		this.contactRepository.giveCreditsToInmates(contactsCreditTransfer).subscribe(() =>
		{
			this.userStore.fetchUser();
			this.transferStep = "TRANSFERRED";
		});
	}

	public getContactsFromQuery(): void
	{
		this.contactRepository.getListedContacts(this.searchQuery).subscribe((contacts: Contact[]) =>
		{
			this.contacts = contacts;
		});
	}

	public getCreditsGivingToUser(userId: string): number
	{
		let currentCreditsToAdd: number = 0;
		if (this.creditMap.has(userId))
		{
			currentCreditsToAdd = this.creditMap.get(userId);
		}
		return currentCreditsToAdd;
	}

	public addCreditsToUser(userId: string): void
	{
		if (this.givingCreditCount < this.userCredits)
		{
			let currentCreditsToAdd: number = 0;
			if (this.creditMap.has(userId))
			{
				currentCreditsToAdd = this.creditMap.get(userId);
			}

			currentCreditsToAdd++;
			this.givingCreditCount++;
			this.creditMap.set(userId, currentCreditsToAdd);
		}
	}

	public subtractCreditsToUser(userId: string): void
	{
		if (this.creditMap.has(userId))
		{
			let currentCreditsToSubtract: number = this.creditMap.get(userId);
			if (currentCreditsToSubtract > 0)
			{
				currentCreditsToSubtract--;
				this.givingCreditCount--;
				this.creditMap.set(userId, currentCreditsToSubtract);
			}
		}
	}

	public addContact(contact: Contact): void
	{
		if (!this.contactIsAdded(contact.contactId))
		{
			this.addedContacts.push(contact);
		}
	}

	public contactIsAdded(contactId: string): boolean
	{
		return this.addedContacts.some((contact: Contact) => contact.contactId === contactId);
	}
}
